/**
 * Lifetime play counters the quests measure against (spec:
 * docs/superpowers/specs/2026-09-08-quests-and-potions-design.md §2).
 * One JSON object under `hambi.stats` (synced: game_saves.stats). Pure —
 * a minimal store is passed in so applyGive and the services share it.
 */
export const STAT_FIELDS = [
  'kills',
  'bossKills',
  'clears',
  'arenaWaves',
  'potionsBought',
  'bagsOpened',
  'obtains',
  'sells',
  'coinsEarned',
] as const;
export type StatField = (typeof STAT_FIELDS)[number];
export type LifetimeStats = Record<StatField, number>;
export const STATS_KEY = 'hambi.stats';

export interface StatsStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

export function emptyStats(): LifetimeStats {
  return Object.fromEntries(STAT_FIELDS.map((f) => [f, 0])) as LifetimeStats;
}

/** Parse `hambi.stats`; every field a non-negative integer, else 0. Never throws. */
export function parseStats(raw: string | null): LifetimeStats {
  const out = emptyStats();
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out;
  const r = parsed as Record<string, unknown>;
  for (const f of STAT_FIELDS) {
    const v = r[f];
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0) out[f] = Math.floor(v);
  }
  return out;
}

/** Add `n` (floored, never negative) to `field` and persist; returns the new stats. */
export function bumpStat(store: StatsStore, field: StatField, n = 1): LifetimeStats {
  const stats = parseStats(store.getItem(STATS_KEY));
  stats[field] += Math.max(0, Math.floor(n));
  store.setItem(STATS_KEY, JSON.stringify(stats));
  return stats;
}
